/**
 * Server-Sent Events aus einer `fetch`-Antwort lesen.
 *
 * `EventSource` kann nur GET und keine Kopfzeilen — die Übersetzung ist aber
 * ein POST mit Nutzlast. Also wird der Strom selbst zerlegt.
 */

export interface SseEreignis {
  name: string
  daten: unknown
}

const FRAME_ENDE = /\r?\n\r?\n/

function zerlegeFrame(frame: string): SseEreignis | null {
  let name = 'message'
  const datenzeilen: string[] = []

  for (const zeile of frame.split(/\r?\n/)) {
    if (zeile.startsWith(':')) continue // Kommentar / Heartbeat
    if (zeile.startsWith('event:')) name = zeile.slice(6).trim()
    else if (zeile.startsWith('data:')) datenzeilen.push(zeile.slice(5).trimStart())
  }

  if (datenzeilen.length === 0) return null

  try {
    return { name, daten: JSON.parse(datenzeilen.join('\n')) }
  } catch {
    // Ein unlesbarer Frame darf den Strom nicht abbrechen.
    return null
  }
}

export async function* leseSse(antwort: Response): AsyncGenerator<SseEreignis> {
  const koerper = antwort.body
  if (!koerper) throw new Error('Die Antwort hat keinen Datenstrom.')

  const leser = koerper.getReader()
  const dekoder = new TextDecoder()
  let puffer = ''

  try {
    for (;;) {
      const { done, value } = await leser.read()
      if (done) break

      puffer += dekoder.decode(value, { stream: true })

      for (;;) {
        const treffer = FRAME_ENDE.exec(puffer)
        if (!treffer) break
        const frame = puffer.slice(0, treffer.index)
        puffer = puffer.slice(treffer.index + treffer[0].length)
        const ereignis = zerlegeFrame(frame)
        if (ereignis) yield ereignis
      }
    }

    // Der Strom kann ohne abschließende Leerzeile enden.
    const rest = zerlegeFrame(puffer.trim())
    if (rest) yield rest
  } finally {
    leser.releaseLock()
  }
}
